'use client';

import React, { useEffect, useRef } from "react"
import { MaxWidthWrapper } from "@workspace/ui/molecules/max-width-wrapper";
import { generateBreadcrumbList, maxWidthWrapperClassName, siteSettings } from "@workspace/utils";
import { cn } from "@workspace/utils";
import { ProductHomeHero } from "@/components/ProductHomeHero";
import { IndustrySolutions } from "@/components/IndustrySolutions";
import { IndustryBenefits, IndustryBenefitsProps } from "@/components/IndustryBenefits";
import { courses } from "@/constant/courses";
import { isEmpty } from "lodash";
import { useParams } from "next/navigation";
import { NotFound } from "../NotFound";
import { useAnalytics, useIntersectionObserver } from "@/hooks/usePerformance";

// These below-the-fold sections are imported statically (not via next/dynamic).
// next/dynamic inside this Client Component rendered only its short `loading`
// stub into the prerendered HTML even with ssr:true — when the real, much taller
// sections hydrated they shoved the footer down ~650px (Cumulative Layout Shift
// ~0.8). Static imports are always server-rendered at their true height, so the
// page layout is stable from first paint.
import { IndustryFeatures } from "@/components/IndustryFeatures";
import { HomeFAQ } from "@/components/faq";
import { CtaButtonsBig } from "@/components/CtaBig/optimized-index";
import { CalcPricingPlans, PricingPlans } from "@/components/pricing";
import { WhyUs } from "@/components/WhyUs";

// FAQ answers are authored as JSX, so we recursively extract their plain text
// to emit valid FAQPage structured data (answers must be strings in JSON-LD).
function nodeToText(node: React.ReactNode): string {
    if (node === null || node === undefined || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(nodeToText).join("");
    if (React.isValidElement(node)) {
        const el = node as React.ReactElement<{ children?: React.ReactNode }>;
        if (el.type === "br") return " ";
        return nodeToText(el.props.children);
    }
    return "";
}

type FaqCategory = {
    category?: string;
    items?: { question: string; answer: React.ReactNode }[];
};

// Course metadata for analytics
const courseMetadata: Record<string, {
    programType: string;
    targetAudience: string;
    duration: string;
}> = {
    'master-of-business-administration': {
        programType: 'MBA',
        targetAudience: 'Working professionals with 3-10 years experience',
        duration: '10-24 months'
    },
    'master-in-management': {
        programType: 'MiM',
        targetAudience: 'Recent graduates with 0-3 years experience',
        duration: '12-24 months'
    },
    'master-in-finance': {
        programType: 'MFin',
        targetAudience: 'Early professionals with 1-5 years experience',
        duration: '10-18 months'
    },
    'undergraduate-bachelors-abroad': {
        programType: 'Undergraduate',
        targetAudience: 'High school students and parents',
        duration: '3-4 years'
    },
    'specialised-masters': {
        programType: 'Specialized Masters',
        targetAudience: 'Early professionals with 1-5 years experience',
        duration: '10-24 months'
    },
    'master-in-marketing': {
        programType: 'Marketing Masters',
        targetAudience: 'Early professionals with 1-5 years experience',
        duration: '10-18 months'
    }
};

export function AdmissionsConsulting() {
    const params = useParams();
    const rawSlug = params?.slug;
    const slug: string = (Array.isArray(rawSlug) ? rawSlug[0] : rawSlug) ?? '';
    const { trackEvent, trackPageView } = useAnalytics();

    // Refs for section tracking
    const heroRef = useRef<HTMLDivElement>(null);
    const benefitsRef = useRef<HTMLDivElement>(null);
    const pricingRef = useRef<HTMLDivElement>(null);
    const faqRef = useRef<HTMLDivElement>(null);

    // Track section visibility
    const heroVisible = useIntersectionObserver(heroRef, { threshold: 0.5 });
    const benefitsVisible = useIntersectionObserver(benefitsRef, { threshold: 0.3 });
    const pricingVisible = useIntersectionObserver(pricingRef, { threshold: 0.3 });
    const faqVisible = useIntersectionObserver(faqRef, { threshold: 0.3 });

    const currentCourse = courses[slug as keyof typeof courses] as
        | (typeof courses)[keyof typeof courses]
        | undefined;
    const programMeta = courseMetadata[slug as keyof typeof courseMetadata] || null;

    // Track page view on mount
    useEffect(() => {
        if (currentCourse) {
            trackPageView(
                `/admissions-consulting/${slug}`,
                typeof document !== 'undefined' ? document.title : 'Admissions Consulting'
            );

            trackEvent('program_page_viewed', {
                program_slug: slug,
                program_type: programMeta?.programType,
                page_category: 'admissions_consulting'
            });
        }
    }, [slug, currentCourse, programMeta, trackPageView, trackEvent]);

    // Track section views
    useEffect(() => {
        if (heroVisible) {
            trackEvent('section_viewed', {
                section_name: 'hero',
                program_slug: slug,
                program_type: programMeta?.programType
            });
        }
    }, [heroVisible, slug, programMeta, trackEvent]);

    useEffect(() => {
        if (benefitsVisible) {
            trackEvent('section_viewed', {
                section_name: 'benefits',
                program_slug: slug,
                program_type: programMeta?.programType
            });
        }
    }, [benefitsVisible, slug, programMeta, trackEvent]);

    useEffect(() => {
        if (pricingVisible) {
            trackEvent('section_viewed', {
                section_name: 'pricing',
                program_slug: slug,
                program_type: programMeta?.programType,
                engagement_signal: 'high_intent'
            });
        }
    }, [pricingVisible, slug, programMeta, trackEvent]);

    useEffect(() => {
        if (faqVisible) {
            trackEvent('section_viewed', {
                section_name: 'faq',
                program_slug: slug,
                program_type: programMeta?.programType
            });
        }
    }, [faqVisible, slug, programMeta, trackEvent]);

    if (!currentCourse || isEmpty(currentCourse)) {
        trackEvent('404_error', {
            page_path: `/admissions-consulting/${slug}`,
            error_type: 'course_not_found'
        });
        return <NotFound />;
    }

    // Generate course-specific structured data
    const courseStructuredData = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": currentCourse.productHomeHeroConfig?.title || "Admissions Consulting",
        "description": currentCourse.productHomeHeroConfig?.description?.replace(/<[^>]*>/g, '') || "",
        "provider": {
            "@type": "Organization",
            "name": siteSettings.siteName,
            "sameAs": siteSettings.canonical
        },
        ...(programMeta && {
            "courseMode": "Online",
            "educationalLevel": programMeta.programType,
            "timeRequired": programMeta.duration,
            "audience": {
                "@type": "EducationalAudience",
                "educationalRole": programMeta.targetAudience
            }
        }),
        "offers": {
            "@type": "Offer",
            "category": "Educational Consulting",
            "availability": "https://schema.org/InStock"
        }
    };

    const breadcrumbSchema = generateBreadcrumbList([
        { name: "Home", url: siteSettings.canonical },
        { name: "Admissions Consulting", url: `${siteSettings.canonical}admissions-consulting/` },
        {
            name: currentCourse.productHomeHeroConfig?.title || "Program",
            url: `${siteSettings.canonical}admissions-consulting/${slug}`
        },
    ]);

    // FAQPage structured data — built from the program's own FAQ content so AI
    // answer engines (AI Overviews, ChatGPT/Perplexity) can quote it directly.
    const faqCategories = (currentCourse.faqs ?? []) as FaqCategory[];
    const faqStructuredData = faqCategories.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqCategories.flatMap((cat) =>
                (cat.items ?? []).map((item) => ({
                    "@type": "Question",
                    "name": item.question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": nodeToText(item.answer).replace(/\s+/g, " ").trim(),
                    },
                }))
            ),
        }
        : null;

    return (
        <div className="">
            {/* Hero Section */}
            <div ref={heroRef}>
                {!isEmpty(currentCourse.productHomeHeroConfig) && (
                    <ProductHomeHero
                        {...currentCourse.productHomeHeroConfig}
                    />
                )}
            </div>

            <MaxWidthWrapper className={cn("relative mt-4", maxWidthWrapperClassName)}>
                {/* Solutions Section */}
                {!isEmpty(currentCourse.industrySolutionsConfig) && (
                    <IndustrySolutions
                        industrySolutionsConfig={
                            currentCourse.industrySolutionsConfig as React.ComponentProps<
                                typeof IndustrySolutions
                            >['industrySolutionsConfig']
                        }
                    />
                )}

                {/* Benefits Section */}
                <div ref={benefitsRef}>
                    {!isEmpty(currentCourse.industryBenefitsConfig) && (
                        <IndustryBenefits {...currentCourse.industryBenefitsConfig as IndustryBenefitsProps} />
                    )}
                </div>
            </MaxWidthWrapper>

            {/* Why Us comparison table — full-width band directly above Pricing.
                The #why-us anchor is the scroll target for the "Why US?" CTAs. */}
            <WhyUs />

            <MaxWidthWrapper className={cn("relative", maxWidthWrapperClassName)}>
                {/* Pricing Section */}
                <div ref={pricingRef}>
                    <div className="max-width-large align-center" id="pricing-plan" >
                        <div className="text-align-center">
                            <h2 className="text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
                                {currentCourse.pricingTitle}
                            </h2>
                            <div className="padding-bottom padding-xlarge"></div>
                        </div>
                    </div>
                    {currentCourse?.pricingKey && (
                        <div className="pricing-plan_wrapper">
                            {/* No <Suspense> here: the pricing components read URL
                                params on the client (useClientSearchParams) so they
                                no longer suspend. A Suspense boundary would stream
                                this content into a hidden <template> that JS reveals
                                post-load, causing a large layout shift. */}
                            <CalcPricingPlans
                                course={currentCourse.pricingKey}
                            />
                            <PricingPlans
                                course={currentCourse.pricingKey}
                            />
                        </div>
                    )}
                </div>
            </MaxWidthWrapper>

            {/* Features Section */}
            {!isEmpty(currentCourse.industryFeaturesConfig) && (
                <IndustryFeatures
                    featuresTitle={currentCourse.industryFeaturesTitle}
                    features={currentCourse.industryFeaturesConfig}
                />
            )}

            {/* FAQ Section */}
            <div ref={faqRef}>
                {!isEmpty(currentCourse.faqs) && (
                    <HomeFAQ faqs={currentCourse.faqs} />
                )}
            </div>

            {/* Final CTA */}
            {!isEmpty(currentCourse.ctaConfig) && (
                <CtaButtonsBig
                    cta={currentCourse.ctaConfig}
                />
            )}

            {/* Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(breadcrumbSchema).replace(/</g, '\\u003c')
                }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(courseStructuredData).replace(/</g, '\\u003c')
                }}
            />
            {faqStructuredData && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(faqStructuredData).replace(/</g, '\\u003c')
                    }}
                />
            )}
        </div>
    )
}